import classNames from "classnames"

import React from "react"

import TestimonialCard, { TestimonialProps } from "./TestimonialCard"
import { Color } from "../../../../constants/V2/color"
import "./styles.css"
import NumberDisplay from "../../../elements/V2/NumberDisplay"
import LayoutHeader, { LayoutHeaderProps } from "../../LayoutComponents/Header"
import LayoutItem from "../../Layouts/LayoutItem"
import SocialProofingSlider from "../SocialProofing/SocialProofingSlider"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"

type Props = {
  backgroundColor?: Color
  cardColor?: Color
  cardBorderColor?: Color
  cardDropShadow?: boolean
  testimonials: TestimonialProps[]
  hideStars: boolean
  heading?: LayoutHeaderProps
  price?: number
  priceDescription?: string
  ctaText?: string
  ctaLink?: YocoCom.YocoLink
}

const TestimonialSwiper = ({
  backgroundColor = Color.Charcoal,
  cardColor = Color.Coal,
  cardBorderColor,
  cardDropShadow = false,
  testimonials,
  hideStars,
  heading,
  price,
  priceDescription,
  ctaText,
  ctaLink,
  ...props
}: Props) => {
  const hasHeadingContent = Boolean(
    heading?.superscriptText ||
      heading?.headingText ||
      heading?.headingBodyText ||
      heading?.callout
  )
  const hasPrice = typeof price === "number" && Number.isFinite(price)
  const hasHeaderSection = hasHeadingContent || hasPrice

  const accentBackgroundColor = getAccentColor(backgroundColor)
  const accentCardColor = getAccentColor(
    cardColor ? cardColor : backgroundColor
  )

  return (
    <LayoutItem
      as="div"
      animateOnScroll
      className={classNames(
        "testimonial-edge-fade relative col-span-full -mx-gutter-xs inline-block max-w-max overflow-hidden rounded-32 sm:mx-0",
        accentBackgroundColor,
        getBackgroundColorClass(backgroundColor)
      )}
      data-testid="social-proofing-component"
      {...props}
    >
      {hasHeaderSection && (
        <div
          className={classNames(
            "flex flex-col items-center px-gutter-xs sm:px-32 lg:px-72",
            hasHeadingContent ? "pt-24 sm:pt-80" : "pt-8 sm:pt-12"
          )}
        >
          {hasHeadingContent && heading && (
            <LayoutHeader
              {...heading}
              superscriptColor={
                heading.superscriptColor || accentBackgroundColor
              }
              headingTextColor={
                heading.headingTextColor || accentBackgroundColor
              }
              headingBodyColor={
                heading.headingBodyColor || accentBackgroundColor
              }
            />
          )}
          {hasPrice && (
            <NumberDisplay
              type="price"
              size="matchFont"
              valueFontSize="h2"
              value={price}
              countUpOnView
              adornmentText="R"
              adornmentTextPosition="left"
              enableThousandSeparator
              textColor={accentBackgroundColor}
              alignment="center"
              subscriptText={priceDescription}
            />
          )}
        </div>
      )}
      <div className="relative py-56">
        {/* Mobile */}
        <SocialProofingSlider
          className="block overflow-visible sm:hidden"
          slideClassName="!w-[300px]"
          useLoopBuffer
        >
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              {...testimonial}
              key={`testimonial-${index}`}
              textColor={accentCardColor}
              cardColor={cardColor}
              cardBorderColor={cardBorderColor}
              cardDropShadow={cardDropShadow}
              hideStars={hideStars}
            />
          ))}
        </SocialProofingSlider>
        {/* Desktop */}
        <SocialProofingSlider
          className="hidden sm:block"
          autoplay
          useLoopBuffer
        >
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              {...testimonial}
              key={`testimonial-${index}`}
              textColor={accentCardColor}
              cardColor={cardColor}
              cardBorderColor={cardBorderColor}
              cardDropShadow={cardDropShadow}
              hideStars={hideStars}
            />
          ))}
        </SocialProofingSlider>

        {ctaLink && ctaText && (
          <div className="mt-[46px] flex justify-center px-gutter-xs pb-24">
            <PillButton
              text={ctaText}
              linkUrl={ctaLink}
              size="medium"
              style="solid"
              color={Color.Blue}
            />
          </div>
        )}
      </div>
    </LayoutItem>
  )
}

export default TestimonialSwiper
